/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';

import IconClose from './icons/IconClose';

interface Props {
  value: any;
  name: string;
  type: string;
  autoComplete?: string;
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  autoFocus?: boolean;
  readOnly?: boolean;
  onFocus?(e): void;
  onBlur?(e): void;
  onChange?(e): void;
  placeholder?: string;
  style?: object;
  className?: string;
  inputClassName?: string;
  label?: any;

  attemptsBeforeInvalidHint?: number;
  alwaysFloating?: boolean;
  clearValue?(): void;
  regularHint?: any;
  initialFocus?: boolean;
  invalidHint?: any;
  isInvalid?: boolean;
  maxLength?: number;
  prefix?: any;
  selectAllonFocus?: boolean;
  isClearable?: boolean;
  alwayFocus?: boolean;

  hasFixedLabel?: boolean;
  hideLabel?: boolean;
  noForcedLabel?: boolean;
  colorTheme?: 'default' | 'light';
  noForcedHeight?: boolean;
  inputIconComponent?: any;
  hasInvalidLine: boolean;
}

interface State {
  hasFocus: boolean;
  attempts: number;
}

class BasicTextInput extends React.Component<Props, State> {

  public removeCancelDelay: number = null;
  private inputRef = React.createRef<HTMLInputElement>();

  static defaultProps = {
    attemptsBeforeInvalidHint: 0,
    alwayFocus: false,
    hasInvalidLine: true,
  };

  state: State = {
    hasFocus: this.props.initialFocus || this.props.alwayFocus,
    attempts: 0,
  };

  componentDidMount() {
    if (this.props.initialFocus) {
      this.inputRef.current.focus();
    }
    if (this.props.selectAllonFocus) {
      this.inputRef.current.select();
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.removeCancelDelay);
    this.removeCancelDelay = null;
  }

  handleOnFocus = (event) => {
    if (this.removeCancelDelay) {
      window.clearTimeout(this.removeCancelDelay);
      this.removeCancelDelay = null;
    }

    this.setState({
      hasFocus: true,
    });

    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  }

  handleOnBlur = (event) => {
    const attempts = !_.isEmpty(this.props.value) ? this.state.attempts + 1 : this.state.attempts;
    this.removeCancelDelay = window.setTimeout(() => {
      window.clearTimeout(this.removeCancelDelay);
      this.removeCancelDelay = null;
      this.setState({
        hasFocus: this.props.alwayFocus,
        attempts,
      });
    }, 250);

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  handleOnChange = (event) => {
    if (this.props.onChange) {
      this.props.onChange(event);
    }
  }

  shouldDisplayInvalid = () => {
    return this.state.attempts >= this.props.attemptsBeforeInvalidHint;
  }

  shouldDisplayInvalidMessage = () => {
    return this.shouldDisplayInvalid() && !_.isEmpty(this.props.invalidHint);
  }

  clearField = (event) => {
    event.preventDefault();
    if (this.props.clearValue) {
      this.props.clearValue();
    }
    this.inputRef.current.focus();
  }

  renderRegularHint = () => {
    return (
      this.props.regularHint ? (
        <div>
          {this.props.regularHint}
        </div>
      ) : null
    );
  }

  renderCancelButton = () => {
    return (
      (this.props.isClearable && this.props.value && this.state.hasFocus) ? (
        <div className="cmp-textinput-action" onClick={this.clearField}>
          <IconClose.Small color="gsMiddle" />
        </div>
      ) : null
    );
  }

  render() {
    const isInvalid = this.shouldDisplayInvalid() && this.props.isInvalid;
    const styleClass = classnames(
      'cmp-textinput', {
        'hide-label': this.props.hideLabel,
        'has-floating-label': this.props.hasFixedLabel,
        'with-data': this.state.hasFocus || !_.isEmpty(this.props.value) || _.isNumber(this.props.value),
        'is-light': this.props.colorTheme === 'light',
        'with-forced-label': !this.props.noForcedLabel,
        'always-floating': this.props.alwaysFloating,
        'invalid-border': this.props.hasInvalidLine && isInvalid,
      });

    return (
      <div className={classnames('cmp-textinput-wrapper', this.props.className, { 'is-invalid': isInvalid })}>
        <div className={styleClass}>
          {
            this.props.prefix ? (
              <span className="cmp-textinput-prefix">{this.props.prefix}</span>
            ) : null
          }
          <input
            ref={this.inputRef}
            type={this.props.type}
            name={this.props.name}
            className={this.props.inputClassName}
            autoComplete={this.props.autoComplete}
            autoCapitalize={this.props.autoCapitalize}
            value={this.props.value}
            autoFocus={this.props.autoFocus}
            readOnly={this.props.readOnly}
            onFocus={this.handleOnFocus}
            onBlur={this.handleOnBlur}
            onChange={this.handleOnChange}
            placeholder={this.props.placeholder}
            style={this.props.style}
            maxLength={this.props.maxLength}
          />

          <label htmlFor={this.props.name}>
            {this.props.label}
          </label>

          {
            this.props.inputIconComponent ? (
              <div className="cmp-textinput-action">
                {this.props.inputIconComponent}
              </div>
            ) : this.renderCancelButton()
          }
        </div>

        <div className="cmp-textinput-caption">
        {
          this.props.isInvalid && this.shouldDisplayInvalidMessage() ?
            this.props.invalidHint : this.renderRegularHint()
        }
        </div>

        <div className="cmp-input-messages">
          {this.props.children}
        </div>
      </div>
    );
  }
}

const TextInput = (props) => (
  <BasicTextInput inputClassName="__standard_text" {...props} />
);

export const NumberInput = (props) => (
  <BasicTextInput inputClassName="__standard_text" {...props} type="tel" />
);

export default TextInput;
